import type { SupplyHistory, SupplyItem } from '../types';
import * as supplyApi from '../api/supply';
import { KeyedAsyncCache } from './asyncCache';

const HISTORY_TYPE_FROM_BACKEND: Record<supplyApi.BackendSupplyTransactionType, SupplyHistory['loai']> = {
  IN: 'Nhập',
  OUT: 'Xuất',
  ADJUST: 'Điều chỉnh',
};

function toFrontendSupplyHistory(t: supplyApi.BackendSupplyTransaction): SupplyHistory {
  return {
    id: t._id,
    supplyId: t.supplyItemId,
    loai: HISTORY_TYPE_FROM_BACKEND[t.type],
    soLuong: t.quantity,
    soLuongCu: t.stockBefore,
    soLuongMoi: t.stockAfter,
    ngay: t.createdAt,
    nongHoId: t.farmerId,
    lyDo: t.reason,
    nguoiThucHien: t.performedBy,
  };
}

const historyBySupplyItem = new KeyedAsyncCache<SupplyHistory[]>(
  async (supplyItemId) => (await supplyApi.getSupplyItemHistory(supplyItemId)).map(toFrontendSupplyHistory),
  [],
  'supply:historyBySupplyItem',
);

export function getSupplyHistoriesBySupply(supplyItemId: string): SupplyHistory[] {
  return historyBySupplyItem.get(supplyItemId);
}

const STATUS_FROM_BACKEND: Record<supplyApi.BackendSupplyItemStatus, SupplyItem['status']> = {
  SUFFICIENT: 'Đủ',
  LOW_STOCK: 'Sắp hết',
  OUT_OF_STOCK: 'Hết hàng',
  DISCONTINUED: 'Ngừng cung ứng',
};

function toFrontendSupplyItem(s: supplyApi.BackendSupplyItem): SupplyItem {
  return {
    id: s._id,
    maVatTu: s.code,
    name: s.name,
    nhom: s.group || '',
    stock: s.stock,
    available: s.available,
    reserved: s.reserved,
    unit: s.unit,
    giaNhap: s.purchasePriceVnd,
    giaCungUng: s.supplyPriceVnd,
    nhaCungCap: s.supplier,
    supplierId: s.supplierId,
    loHang: s.batchNo,
    hanSuDung: s.expiryDate,
    status: STATUS_FROM_BACKEND[s.status] ?? 'Đủ',
    ngưỡngCảnhBáo: s.lowStockThreshold,
    ghiChu: s.notes,
  };
}

const byHtx = new KeyedAsyncCache<SupplyItem[]>(
  async (htxId) => (await supplyApi.listSupplyItemsByHtx(htxId)).map(toFrontendSupplyItem),
  [],
  'supply:byHtx',
);

export function getSupplyItemsByHtx(htxId?: string): SupplyItem[] {
  if (!htxId) return [];
  return byHtx.get(htxId);
}

function refresh(htxId: string, supplyItemId?: string) {
  byHtx.invalidate(htxId);
  byHtx.get(htxId);
  if (supplyItemId) {
    historyBySupplyItem.invalidate(supplyItemId);
    historyBySupplyItem.get(supplyItemId);
  }
}

export async function addSupplyItem(
  data: Pick<SupplyItem, 'maVatTu' | 'name' | 'nhom' | 'unit'> & Partial<Pick<SupplyItem, 'stock' | 'giaNhap' | 'giaCungUng' | 'nhaCungCap' | 'supplierId' | 'loHang' | 'hanSuDung' | 'ngưỡngCảnhBáo' | 'ghiChu'>>,
  htxId: string,
): Promise<SupplyItem> {
  const created = await supplyApi.createSupplyItem({
    htxId,
    code: data.maVatTu,
    name: data.name,
    group: data.nhom || undefined,
    initialStock: data.stock,
    unit: data.unit,
    purchasePriceVnd: data.giaNhap,
    supplyPriceVnd: data.giaCungUng,
    supplier: data.nhaCungCap,
    supplierId: data.supplierId,
    batchNo: data.loHang,
    expiryDate: data.hanSuDung,
    lowStockThreshold: data.ngưỡngCảnhBáo,
    notes: data.ghiChu,
  });
  refresh(htxId);
  return toFrontendSupplyItem(created);
}

/** Edits the descriptive fields of an item that already exists. Stock/available
 * are deliberately absent: they only ever move through stockIn/stockOut/adjust,
 * each of which writes a ledger row with a reason. */
export async function updateSupplyItem(
  id: string,
  data: Partial<Pick<SupplyItem, 'maVatTu' | 'name' | 'nhom' | 'unit' | 'giaNhap' | 'giaCungUng' | 'nhaCungCap' | 'supplierId' | 'loHang' | 'hanSuDung' | 'ngưỡngCảnhBáo' | 'ghiChu'>>,
  htxId: string,
): Promise<SupplyItem> {
  const updated = await supplyApi.updateSupplyItem(id, {
    code: data.maVatTu,
    name: data.name,
    group: data.nhom,
    unit: data.unit,
    purchasePriceVnd: data.giaNhap,
    supplyPriceVnd: data.giaCungUng,
    supplier: data.nhaCungCap,
    supplierId: data.supplierId,
    batchNo: data.loHang,
    expiryDate: data.hanSuDung,
    lowStockThreshold: data.ngưỡngCảnhBáo,
    notes: data.ghiChu,
  });
  refresh(htxId, id);
  return toFrontendSupplyItem(updated);
}

export async function deleteSupplyItem(id: string, htxId: string): Promise<void> {
  await supplyApi.deleteSupplyItem(id);
  refresh(htxId);
}

export async function stockInSupplyItem(id: string, quantity: number, reason: string, htxId: string): Promise<SupplyItem> {
  const updated = await supplyApi.stockInSupplyItem(id, quantity, reason);
  refresh(htxId, id);
  return toFrontendSupplyItem(updated);
}

export async function stockOutSupplyItem(id: string, quantity: number, reason: string, htxId: string): Promise<SupplyItem> {
  const updated = await supplyApi.stockOutSupplyItem(id, quantity, reason);
  refresh(htxId, id);
  return toFrontendSupplyItem(updated);
}

export async function adjustSupplyItem(id: string, newStock: number, reason: string, htxId: string): Promise<SupplyItem> {
  const updated = await supplyApi.adjustSupplyItem(id, newStock, reason);
  refresh(htxId, id);
  return toFrontendSupplyItem(updated);
}

/** Issuing to a farmer is one atomic backend action (decrements this SupplyItem's
 * stock AND creates the farmer's Material record server-side) — do not also call
 * stockOutSupplyItem/addMaterial separately for this path. */
export async function issueSupplyToFarmer(
  supplyItemId: string,
  farmerId: string,
  quantity: number,
  htxId: string,
  seasonId?: string,
  notes?: string,
): Promise<supplyApi.BackendMaterial> {
  const material = await supplyApi.issueMaterialToFarmer({ supplyItemId, farmerId, quantity, seasonId, notes });
  refresh(htxId, supplyItemId);
  return material;
}
